import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'CypressId';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { applyCypressData } from 'Utilities/index';

import { DOCUMENT_CAPTURE_RESPONSE_API_KEYS, DOCUMENT_CAPTURE_ROUTE_NAMES, resolveDocumentCaptureRoutesState, resolveSummaryTileReactivation } from '../DocumentCapture.helpers';
import LocalTexts from '../DocumentCapture.texts.json';
import { DocumentCaptureConfirmationProgressContext } from '../DocumentCaptureContexts';
import DocumentCaptureButtons from '../shared/DocumentCaptureButtons/DocumentCaptureButtons';
import DocumentCaptureHeaderAndSummary from '../shared/DocumentCaptureHeaderAndSummary/DocumentCaptureHeaderAndSummary';
import { DocumentCaptureViewContainer } from '../shared/DocumentCaptureViewContainer/DocumentCaptureViewContainer';
import SummaryTiles from '../SummaryTiles/SummaryTiles';

import styles from './SummaryOfSale.scss';

const LocalT = new MDText(LocalTexts.confirmSaleSummary);

type Section = {
  isCompleted?: boolean;
  isReactivated?: boolean;
  label: string;
  node?: string;
  path?: string;
  status?: string;
}

export const SummaryOfSale = () => {
  const summarySections = [
    {
      label: LocalT.translate('sections.confirmSaleDRFT') as string,
      node: DOCUMENT_CAPTURE_RESPONSE_API_KEYS.CONFIRMED_SALE,
      path: DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE,
      status: LocalT.translate('status.confirmed') as string,
    },
    {
      label: LocalT.translate('sections.bankDetails') as string,
      node: DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_ADDED,
      path: DOCUMENT_CAPTURE_ROUTE_NAMES.ADD_BANK_DETAILS,
      reactivatedNode: DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_REVIEW_FAILED,
      status: LocalT.translate('status.confirmed') as string,
    },
    {
      label: LocalT.translate('sections.documents') as string,
      node: DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_FULFILLED,
      path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DOCUMENTS,
      reactivatedNode: DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS,
      status: LocalT.translate('status.uploaded') as string,
    },
  ];
  const documentCaptureConfirmationProgressContext = useContext(
    DocumentCaptureConfirmationProgressContext,
  );
  const history = useHistory();
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { vrm } = vehicle;
  const REVIEW_STATUS_PATH = `/${vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.REVIEW_STATUS}`;

  const progressState = (
    resolveDocumentCaptureRoutesState(documentCaptureConfirmationProgressContext.progress)
  );

  const {
    [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_FULFILLED]: documentCaptureCompleted,
    [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_ADDED]: bankDetailsAdded,
  } = progressState;

  const sections: Section[] = summarySections.map((section) => ({
    ...section,
    isCompleted: progressState[section.node] as boolean,
    isReactivated: resolveSummaryTileReactivation(
      { node: section?.reactivatedNode, state: progressState },
    ),
  }));

  const onTileClick = () => {
    history.push(`/${vrm}/upload-documents`);
  };

  const completedAllSteps = documentCaptureCompleted && bankDetailsAdded;

  const title = LocalT.translate('titleDRFT') as string;

  const summary = LocalT.translate('summaryDRFT') as string;

  return (
    <DocumentCaptureViewContainer>
      <div className={styles.summary}>
        <DocumentCaptureHeaderAndSummary
          header={title}
          summary={!completedAllSteps && <p>{summary}</p>}
        />
        <SummaryTiles onClick={onTileClick} {...{ sections }} />
        <DocumentCaptureButtons>
          <Button
            fullWidth
            as={Link}
            {...applyCypressData(cypressIds.buttons.saleContinueButton)}
            icon="chevron"
            label={LocalT.translate(completedAllSteps ? 'continue' : 'saveAndExit') as string}
            to={completedAllSteps ? REVIEW_STATUS_PATH : '/account'}
          />
        </DocumentCaptureButtons>
      </div>
    </DocumentCaptureViewContainer>
  );
};
